import type { QueryClient } from '@tanstack/react-query';
import type { Row } from './db';
import { supabase } from './supabase';

/** Order reads for the POS and the Orders page. Writes go through the outbox (runQueuedCommand). */
export type Order = Pick<
  Row<'orders'>,
  | 'id'
  | 'location_id'
  | 'terminal_id'
  | 'order_number'
  | 'business_date'
  | 'type'
  | 'status'
  | 'payment_status'
  | 'fulfilment_status'
  | 'dining_table_id'
  | 'covers'
  | 'customer_name'
  | 'scheduled_for'
  | 'notes'
  | 'currency'
  | 'subtotal_minor'
  | 'total_minor'
  | 'paid_minor'
  | 'refunded_minor'
  | 'written_off_minor'
  | 'version'
  | 'placed_at'
  | 'sent_at'
  | 'paid_at'
  | 'served_at'
  | 'first_served_at'
  | 'completed_at'
  | 'cancelled_at'
  | 'voided_at'
  | 'cancel_reason'
  | 'void_reason'
  | 'created_by_employee_id'
  | 'table_session_id'
>;
export type OrderItem = Pick<
  Row<'order_item'>,
  | 'id'
  | 'order_id'
  | 'seq'
  | 'product_variant_id'
  | 'name_snapshot'
  | 'variant_name_snapshot'
  | 'production_route_code_snapshot'
  | 'qty'
  | 'unit_price_minor'
  | 'modifiers_unit_minor'
  | 'line_total_minor'
  | 'notes'
  | 'status'
  | 'sent_at'
  | 'void_reason'
>;
export type OrderItemModifier = Pick<
  Row<'order_item_modifier'>,
  | 'id'
  | 'order_item_id'
  | 'group_name_snapshot'
  | 'option_name_snapshot'
  | 'price_delta_minor'
  | 'qty'
>;
export type Ticket = Pick<
  Row<'production_ticket'>,
  | 'id'
  | 'order_id'
  | 'production_station_id'
  | 'ticket_seq'
  | 'is_addition'
  | 'status'
  | 'sent_at'
  | 'started_at'
  | 'ready_at'
  | 'served_at'
  | 'first_served_at'
>;
export type OrderEvent = Pick<
  Row<'order_event'>,
  'id' | 'event_type' | 'occurred_at' | 'payload' | 'station_id'
>;

export const ORDER_SELECT =
  'id, location_id, terminal_id, order_number, business_date, type, status, payment_status, fulfilment_status, dining_table_id, covers, customer_name, scheduled_for, notes, currency, subtotal_minor, total_minor, paid_minor, refunded_minor, written_off_minor, version, placed_at, sent_at, paid_at, served_at, first_served_at, completed_at, cancelled_at, voided_at, cancel_reason, void_reason, created_by_employee_id, table_session_id';
const ITEM_SELECT =
  'id, order_id, seq, product_variant_id, name_snapshot, variant_name_snapshot, production_route_code_snapshot, qty, unit_price_minor, modifiers_unit_minor, line_total_minor, notes, status, sent_at, void_reason';

export const orderKeys = {
  all: ['orders'] as const,
  open: (locationId: string) => ['orders', 'open', locationId] as const,
  recent: (locationId: string) => ['orders', 'recent', locationId] as const,
  detail: (orderId: string) => ['orders', 'detail', orderId] as const,
};

/**
 * The order's version as the server last gave it to this screen, read when a command is sent. A sheet can open on an
 * older read: after a payment, realtime hints can cancel the order's refetch while its payments are already read again
 * (lib/freshRead.ts), and the screen keeps reading while the sheet is open. Never a read made for sending: a version
 * the screen has not received stays the server's to refuse ("order changed on another device").
 */
export function latestOrderVersion(qc: QueryClient, orderId: string, opened: number): number {
  return qc.getQueryData<OrderDetail>(orderKeys.detail(orderId))?.order.version ?? opened;
}

async function rows<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export const fetchOpenOrders = (locationId: string) =>
  rows<Order>(
    supabase
      .from('orders')
      .select(ORDER_SELECT)
      .eq('location_id', locationId)
      .eq('status', 'open')
      .order('placed_at', { ascending: false }),
  );

export const fetchRecentOrders = (locationId: string) =>
  rows<Order>(
    supabase
      .from('orders')
      .select(ORDER_SELECT)
      .eq('location_id', locationId)
      .order('placed_at', { ascending: false })
      .limit(100),
  );

export interface OrderDetail {
  order: Order;
  items: OrderItem[];
  modifiers: OrderItemModifier[];
  tickets: Ticket[];
  events: OrderEvent[];
}

export async function fetchOrderDetail(orderId: string): Promise<OrderDetail> {
  const { data: order, error } = await supabase
    .from('orders')
    .select(ORDER_SELECT)
    .eq('id', orderId)
    .single();
  if (error) throw error;
  const items = await rows<OrderItem>(
    supabase.from('order_item').select(ITEM_SELECT).eq('order_id', orderId).order('seq'),
  );
  const ids = items.map((i) => i.id);
  const modifiers = ids.length
    ? await rows<OrderItemModifier>(
        supabase
          .from('order_item_modifier')
          .select(
            'id, order_item_id, group_name_snapshot, option_name_snapshot, price_delta_minor, qty',
          )
          .in('order_item_id', ids),
      )
    : [];
  const tickets = await rows<Ticket>(
    supabase
      .from('production_ticket')
      .select(
        'id, order_id, production_station_id, ticket_seq, is_addition, status, sent_at, started_at, ready_at, served_at, first_served_at',
      )
      .eq('order_id', orderId)
      .order('ticket_seq'),
  );
  const events = await rows<OrderEvent>(
    supabase
      .from('order_event')
      .select('id, event_type, occurred_at, payload, station_id')
      .eq('order_id', orderId)
      .order('recorded_at'),
  );
  return { order, items, modifiers, tickets, events };
}

/** Parse the JSON hint app.fail attaches (e.g. `{ "requires": "order.send_unpaid" }`). */
export function hintOf(err: { hint?: unknown }): Record<string, unknown> {
  const h = err.hint;
  if (h && typeof h === 'object') return h as Record<string, unknown>;
  if (typeof h === 'string' && h.trim().startsWith('{')) {
    try {
      return JSON.parse(h) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

export const ORDER_TYPE_LABEL: Record<Order['type'], string> = {
  dine_in: 'Dine-in',
  takeout: 'Takeout',
  advance: 'Advance',
};
export const FULFILMENT_LABEL: Record<Order['fulfilment_status'], string> = {
  not_required: 'no prep',
  not_sent: 'not sent',
  sent: 'sent',
  in_progress: 'in progress',
  partially_ready: 'partly ready',
  ready: 'ready',
  partially_served: 'partly served',
  served: 'served',
};

/** One colour per state on every screen: lib/status.ts (ADR-037). */
export { fulfilmentTone, ticketTone } from './status';
